<template>
  <div class="max-w-[400px] mx-auto px-10">
    <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight py-5">
      <img :src="colorMode.value == 'dark' ? '/images/logos/superseeded_logo_white_small@0.5x.png' : '/images/logos/superseeded_logo_black_small@0.5x.png'" alt="SuperSeeded" class="h-20" />
    </h3>
    <AccountLoginForm
      :prefilled-user="typeof route.query.u === 'string' ? route.query.u : undefined"
      :prefilled-password="typeof route.query.p === 'string' ? route.query.p : undefined"
      :redirect-to="typeof route.query.redirect_to === 'string' ? route.query.redirect_to : undefined"
    />
    <nuxt-link to="/register" class="flex justify-center items-center">
      <Button variant="ghost" class="whitespace-nowrap">
        Don't have an account?
        <Icon name="material-symbols:arrow-right-alt-rounded" size="20" class="mx-3" />
        <span class="underline">Register now</span>
      </Button>
      
    </nuxt-link>
  </div>
</template>

<script setup lang="ts" >
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-vue-next'
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { onKeyStroke } from '@vueuse/core'  
import { useFocus } from '@vueuse/core'
import GetNotified from '@/components/GetNotified.vue'

const username = ref()
const { focused } = useFocus(username, { initialValue: true })

const uiStore = useUiStore()
const { toggleBottomSheet, setBottomDrawerConfig } = uiStore
const bottomDrawer = useBottomDrawer()

const colorMode = useColorMode()

const client = useSupabaseClient()
const user = useSupabaseUser()
const loading = ref(false)
const authError = ref('')
const usingEmailPassword = ref(false)
const showPassword = ref(false)

const formSchema = toTypedSchema(z.object({
    email: z.string().email().optional(),
    password: z.string().min(8).optional(),
}).transform((o) => ({ 
    email: o.email,
    password: o.password 
})))

const apiErrors = ref({
  wrongCredentials: false,
  Provider: false,
  accountNotVerified: false,
  accountSuspended: false,
  accessDenied: false,
  networkError: false
})

const apiErrorMessages = computed(() => {
  return {
    email: apiErrors.value.wrongCredentials ? 'Incorrect username or password' : 
           apiErrors.value.accountSuspended ? 'Account suspended' : 
           apiErrors.value.accessDenied ? 'Access not granted' : 
           apiErrors.value.networkError ? 'Network error occurred' : '',
    password: apiErrors.value.accountNotVerified ? 'Account not verified' : ''
  };
});

const { validate, errors, resetForm, handleSubmit, setErrors, meta: formMeta } = useForm({
  validationSchema: formSchema
})

const route = useRoute()

// Parse OAuth authorization request parameters
const oauthParams = computed(() => {
  const params = route.query
  return {
    response_type: params.response_type as string,
    client_id: params.client_id as string,
    redirect_uri: params.redirect_uri as string,
    state: params.state as string,
    code_challenge: params.code_challenge as string,
    code_challenge_method: params.code_challenge_method as string,
  }
})

// Check if this is an OAuth authorization request from Chainlit
const isOAuthRequest = computed(() => {
  const params = oauthParams.value
  return !!params.client_id && !!params.redirect_uri && !!params.state
})

// Check if this is a callback from Supabase OAuth
const isSupabaseCallback = computed(() => {
  return !!route.query.code && !!route.query.state
})

const showGetNotifiedDrawer = () => {
  bottomDrawer.open({
    title: '',
    hideCloseButton: true,
    component: markRaw(GetNotified),
    props: {
      email: String(route.query?.email ?? '')
    }
  })
}

interface ConfirmResponse {
  status: number;
  data?: {
    allowed: boolean;
  };
  error?: string;
}

const checkAccess = async () => {
  console.log('[DEBUG] checkAccess function called', {
    context: 'login page',
    timestamp: new Date().toISOString(),
    nuxtContext: typeof useNuxtApp !== 'undefined' ? 'available' : 'unavailable'
  })
  
  try {
    console.log('[DEBUG] About to call $fetch in checkAccess (replacing useFetch)')
    const data = await $fetch<ConfirmResponse>('/api/users/confirm', {
      method: 'POST',
      retry: 1
    })
    console.log('[DEBUG] $fetch completed successfully')

    if (!data) {
      console.error('No data received from access check');
      apiErrors.value.networkError = true;
      return false;
    }

    if (data.error) {
      console.error('API error:', data.error);
      apiErrors.value.accessDenied = true;
      return false;
    }

    if (!data.data?.allowed) {
      apiErrors.value.accessDenied = true;
      showGetNotifiedDrawer()
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error checking access:', error);
    apiErrors.value.networkError = true;
    return false;
  }
}

onMounted(() => {
  if (route.query.early_access) {  
    showGetNotifiedDrawer()
  }
  if (route.query.message === 'waitlist') {
    showGetNotifiedDrawer()
  }
})

watchEffect(async () => {
  console.log('[DEBUG] Login page watchEffect triggered', {
    hasUser: !!user.value,
    userEmail: user.value?.email,
    earlyAccess: !!route.query.early_access,
    redirectTo: route.query.redirect_to,
    queryParams: route.query,
    timestamp: new Date().toISOString()
  })
  
  if (user.value && !route.query.early_access) {
    console.log('[DEBUG] About to call checkAccess for logged-in user')
    try {
      // Note: Supabase OAuth callbacks are handled automatically by the Nuxt Supabase module
      // The module will exchange the code for a session and update the user state
      // We don't need to manually handle the callback here

      // Handle OAuth authorization flow if this is an OAuth request from Chainlit
      if (isOAuthRequest.value) {
        console.log('[DEBUG] Handling OAuth authorization request from Chainlit')

        // For OAuth requests, skip the normal access check and proceed to confirm page
        // This allows the OAuth flow to complete without requiring user authentication first
        const redirectToUrl = route.query.redirect_to as string
        const state = route.query.state as string

        console.log('[DEBUG] OAuth request detected, proceeding to confirm page')

        // Navigate to confirm page with OAuth parameters
        const urlParams = new URLSearchParams()
        if (route.query.redirect_uri) {
          urlParams.set('redirect_to', route.query.redirect_uri as string)
        }
        if (state) {
          urlParams.set('state', state)
        }
        if (route.query.client_id) {
          urlParams.set('client_id', route.query.client_id as string)
        }
        const confirmUrl = urlParams.toString() ? `/confirm?${urlParams.toString()}` : '/confirm'
        console.log('[DEBUG] Navigating OAuth request to:', confirmUrl, 'with params:', { redirectToUrl: route.query.redirect_uri, state, clientId: route.query.client_id })

        // Use navigateTo for proper Nuxt navigation with OAuth parameters
        return navigateTo(confirmUrl)
        return
      }

      const { shouldAllowMcpRedirect } = useMcpRedirect()
      const redirectToUrl = route.query.redirect_to as string
      const isMcpRedirect = redirectToUrl && shouldAllowMcpRedirect(redirectToUrl)

      const hasAccess = await checkAccess()
      console.log('[DEBUG] checkAccess result:', hasAccess, 'isMcpRedirect:', isMcpRedirect)

      if (hasAccess || isMcpRedirect) {
        // Preserve both redirect_to and state parameters when navigating to confirm page
        const urlParams = new URLSearchParams()
        if (redirectToUrl) {
          urlParams.set('redirect_to', redirectToUrl)
        }
        if (route.query.state) {
          urlParams.set('state', route.query.state as string)
        }
        const confirmUrl = urlParams.toString() ? `/confirm?${urlParams.toString()}` : '/confirm'
        console.log('[DEBUG] Navigating to:', confirmUrl)
        console.log('[DEBUG] hasAccess:', hasAccess, 'isMcpRedirect:', isMcpRedirect, 'redirectToUrl:', redirectToUrl)

        const code = route.query.code as string | undefined
        if (code) {
          const { data, error } = await client.auth.exchangeCodeForSession(code);
          console.log('[DEBUG] Code exchange result:', data, error)
        } else {
          console.log('[DEBUG] No code in query, skipping exchangeCodeForSession')
        }
        console.log('[DEBUG] route query:', route.query)

        await navigateTo(confirmUrl)
      } else {
        console.log('[DEBUG] Navigation blocked - hasAccess:', hasAccess, 'isMcpRedirect:', isMcpRedirect, 'redirectToUrl:', redirectToUrl)
      }
    } catch (error) {
      console.error('[DEBUG] Error in watchEffect checkAccess:', error)
    }
  }
});

defineOgImageComponent('NuxtSeo', {
    title: 'SuperSeeded.ai',
    description: '',
    theme: '#c8c9d0',
    colorMode: 'light',
})

definePageMeta({
    layout: "auth",
})
</script>
